import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Popover } from '~/components/ui/popover';
import { useApprovals } from '~/hooks/queries/use-approvals';
import { useProBuilderEnabled } from '~/hooks/queries/use-feature-flags';
import { useCodingEnabled } from '~/hooks/queries/use-tenant';
import { useProBuilderOpenStore } from '~/lib/stores/pro-builder-open-store';
import { ActivityBoard } from './activity-board';
import { AskChip } from './ask-chip';
import { BuildButton } from './build-button';
import { NewChatButton } from './new-chat-button';
import { StatusCluster } from './status-cluster';
import { firstAsking, resolveAskChip } from './state-mapping';
import { useChatActivity } from './use-chat-activity';
import { useOpenBuild } from './use-open-build';
import './top-bar.css';

interface ChatTopBarProps {
  /** Starts a brand-new chat — the header already owns this mechanism
   *  (blank asset + `requestOpenChat` + navigate home if elsewhere). */
  onNewChat: () => void;
  /** Opens an existing chat by session id, from any page. */
  onOpenChat: (sessionId: string, title: string | null) => void;
}

/**
 * The chat-nav-in-the-top-bar surface (2026-08-14 new-chatbox-topbar design
 * doc): New chat button, Build button, the status cluster with its activity
 * board dropdown (count-pill header, "New chat / Open all chats →" footer),
 * and ONE ask chip for the first open question — the left group
 * `app-header.tsx` renders. It is unconditional: FRONT-1782 deleted the Chat
 * design preference this used to be gated on, so the top bar is the only
 * chat design.
 *
 * Owns the board's open/closed state, the ⌥N / ⌥B shortcuts, and the data
 * wiring (active chats + their statuses + pending approvals) that feeds the
 * pure `state-mapping` helpers. Opening a CHAT stays with the caller —
 * `app-header.tsx` already has the `useLocation`/`useChatOpenStore` wiring
 * for that. FRONT-1681: opening a BUILD (a Building Session row) goes
 * through `useOpenBuild`.
 *
 * It does NOT publish the browser tab's "(N)" count: the attention store has
 * ONE writer, the sidebar's `useSidebarAsking`; a second one here could zero
 * a count the sidebar still shows.
 */
export function ChatTopBar({ onNewChat, onOpenChat }: ChatTopBarProps) {
  const [boardOpen, setBoardOpen] = useState(false);
  const openBuild = useOpenBuild();
  const navigate = useNavigate();

  // FRONT-1579: the active-chats + statuses → BarChat[] → groups/counts
  // wiring lives in `use-chat-activity.ts`, shared with the chat window's
  // `ChatActivityMenu` so the two boards can't drift apart.
  // FRONT-1681: Building Sessions fold into the same board, gated on
  // `useCodingEnabled()` so a deployment with coding off doesn't poll
  // `/api/coding/sessions` every 15s from this always-mounted header.
  const codingEnabled = useCodingEnabled();
  // FRONT-3040: the Pro Builder row shows for exactly the people the sidebar
  // shows its Pro Builder entry to.
  const proBuilderEnabled = useProBuilderEnabled();
  const { barChats, groups, counts } = useChatActivity({
    includeBuilds: codingEnabled,
    includeProBuilder: proBuilderEnabled,
  });
  const { data: approvals = [] } = useApprovals();

  const askingChat = useMemo(() => firstAsking(barChats), [barChats]);
  const askChip = useMemo(() => resolveAskChip(askingChat, approvals), [askingChat, approvals]);

  // Global ⌥N / Alt+N — matched on e.code because on macOS Option+N types a
  // dead key and e.key is not "n"; the tradeoff is that e.code is the
  // PHYSICAL key, so on a non-QWERTY layout the combo sits on whatever key is
  // at the QWERTY-N position. Cmd/Ctrl+N is browser-reserved and never
  // reaches the page. Keep in sync with NEW_CHAT_SHORTCUT_LABEL in
  // `new-chat-button.tsx`.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!e.altKey || e.metaKey || e.ctrlKey || e.code !== 'KeyN') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      onNewChat();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onNewChat]);

  // ⌥B / Alt+B — the Build button's twin, registered only while building is
  // on for the tenant. Stands down while a full-screen overlay
  // (`role="dialog" aria-modal`) is open, so it never paints a second window
  // on top of a first; Radix popovers carry no `aria-modal`, so the activity
  // board's own popover is let through.
  useEffect(() => {
    if (!codingEnabled) return;
    function onKeyDown(e: KeyboardEvent) {
      if (!e.altKey || e.metaKey || e.ctrlKey || e.code !== 'KeyB') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (document.querySelector('[role="dialog"][aria-modal]')) return;
      e.preventDefault();
      openBuild();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [codingEnabled, openBuild]);

  function openFromBoard(sessionId: string, title: string | null) {
    setBoardOpen(false);
    onOpenChat(sessionId, title);
  }

  function openBuildFromBoard(sessionId: string, _title: string | null) {
    setBoardOpen(false);
    openBuild(sessionId);
  }

  // FRONT-3040: the window is mounted once in the app shell, so this works
  // from any page.
  function openProBuilderFromBoard() {
    setBoardOpen(false);
    useProBuilderOpenStore.getState().openProBuilder();
  }

  function newFromBoard() {
    setBoardOpen(false);
    onNewChat();
  }

  function allChatsFromBoard() {
    setBoardOpen(false);
    navigate('/chats');
  }

  return (
    <div className="eddy-topbar flex min-w-0 flex-1 items-center gap-[10px]">
      <NewChatButton onClick={onNewChat} />
      {/* Gated on the same `useCodingEnabled()` the board's build rows are. */}
      {codingEnabled && <BuildButton onClick={() => openBuild()} />}
      <Popover open={boardOpen} onOpenChange={setBoardOpen}>
        <StatusCluster counts={counts} />
        <ActivityBoard
          groups={groups}
          onOpen={openFromBoard}
          onOpenBuild={openBuildFromBoard}
          onOpenProBuilder={openProBuilderFromBoard}
          onNew={newFromBoard}
          onAllChats={allChatsFromBoard}
        />
      </Popover>
      {askChip && (
        <AskChip
          label={askChip.label}
          action={askChip.action}
          // FRONT-1681: a Building Session goes to openBuild, never
          // onOpenChat; FRONT-3040: the Pro Builder row opens its window.
          onClick={() => {
            if (askChip.kind === 'pro-builder') openProBuilderFromBoard();
            else if (askChip.kind === 'build') openBuild(askChip.sessionId);
            else onOpenChat(askChip.sessionId, askChip.title);
          }}
        />
      )}
    </div>
  );
}
